import { TypedDocumentNode, gql } from '@apollo/client';
import { Outlet } from 'react-router-dom';
import styled from 'styled-components';

import { useTypedParams } from '@sorare/routing';

import { FullWidthContainer } from '@sorare/core/src/atoms/layout/FullWidthContainer';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { HeadlineXL } from '@sorare/core/src/atoms/typography';
import { ANY_SPORT_MY_CLUB_SLUG_HISTORY_FIXTURE } from '@sorare/core/src/constants/__generated__/routes';
import { useCurrentUserContext } from '@sorare/core/src/contexts/currentUser';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { useQueryLoaderResult } from '@sorare/core/src/hooks/useQueryLoaderResult';
import { queryLoader } from '@sorare/core/src/lib/navigation/queryLoader';

import { FixtureSelector } from 'components/fixture/FixtureSelector';
import { NewRewardsBanner } from 'components/rewards/NewRewardsBanner';
import { getFixtureQueryVariables } from 'lib/so5';

import {
  ProHistoryFixtureLayoutQuery,
  ProHistoryFixtureLayoutQueryVariables,
} from './__generated__/index.graphql';

const PRO_HISTORY_FIXTURE_LAYOUT_QUERY = gql`
  query ProHistoryFixtureLayoutQuery(
    $type: So5State
    $slug: String
    $sport: Sport!
  ) {
    currentUser {
      slug
      unclaimedSo5Rewards(sport: $sport) {
        id
        slug
        ...NewRewardsBanner_so5Reward
      }
    }
    so5 {
      so5Fixture(type: $type, slug: $slug, sport: $sport) {
        slug
        displayName
        ...FixtureSelector_so5Fixture
      }
    }
  }
  ${NewRewardsBanner.fragments.so5Reward}
  ${FixtureSelector.fragments.so5Fixture}
` as TypedDocumentNode<
  ProHistoryFixtureLayoutQuery,
  ProHistoryFixtureLayoutQueryVariables
>;

const TitleWrapper = styled.div`
  position: relative;
`;

const StyledHeadlineXL = styled(HeadlineXL)`
  padding: var(--triple-unit) 0;
  font-weight: normal;
`;

export const loaderFn = <Params extends { fixture: string }>() =>
  queryLoader<Params>(
    PRO_HISTORY_FIXTURE_LAYOUT_QUERY,
    ({ fixture, sport }) => ({
      ...getFixtureQueryVariables(fixture),
      sport,
    }),
    {
      preventTransitionBeforeLoaded: true,
    }
  );

type Props = {
  rewardsPoolLink?: React.ReactNode;
};

export const ProHistoryFixtureLayout = <
  Params extends { slug: string; fixture: string },
>({
  rewardsPoolLink,
}: Props) => {
  const { slug } = useTypedParams<Params>();
  const { isMe } = useCurrentUserContext();
  const { currentUser, so5 } =
    useQueryLoaderResult<ProHistoryFixtureLayoutQuery>();
  const { so5Fixture } = so5 || {};
  const { generateSportContextPath } = useSportContext();

  return (
    <Vertical gap={2}>
      <FullWidthContainer.NoPadding>
        {isMe(slug) && (
          <NewRewardsBanner
            so5Rewards={currentUser?.unclaimedSo5Rewards || []}
          />
        )}
        <TitleWrapper>
          <StyledHeadlineXL className="text-center">
            {so5Fixture?.displayName}
          </StyledHeadlineXL>
          {rewardsPoolLink}
        </TitleWrapper>
        <FixtureSelector
          disableUpcoming
          currentFixture={so5Fixture}
          navigateTo={fixtureSlug =>
            generateSportContextPath(ANY_SPORT_MY_CLUB_SLUG_HISTORY_FIXTURE, {
              params: {
                slug,
                fixture: fixtureSlug,
              },
            })
          }
        />
      </FullWidthContainer.NoPadding>
      <Outlet />
    </Vertical>
  );
};
